import { Form } from '@repo/ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PrimaryButton } from '@repo/ui/components/buttons/PrimaryButton';
import { apiPost } from '@repo/lib/axios/axios-utils';
import { endpoints } from '@/lib/axios/axios-global';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SecondaryButton } from '@repo/ui/components/buttons/SecondaryButton';
import CancelPopup from '@repo/ui/components/Popups/CancelPopup';
import SuccessPopup from '@repo/ui/components/Popups/SuccessPopup';
import { Student } from '@/types/student.types';
import { EditStudentFormValues } from '../schemas/edit-student-schema';
import ServiceInfo from './ServiceInfo';
import { EditStudentSchema } from '@/features/studentRelations/forms/schemas/edit-student-schema';
import { AddStudentFormValues, AddStudentSchema } from '@/features/studentRelations/forms/schemas/add-student-schema';



import type { AddServiceRequestFormProps } from '../../../types/component-props.types';
export default function AddServiceRequestForm({ student }: AddServiceRequestFormProps) {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showCancelPopup, setShowCancelPopup] = useState(false);
    const navigate = useNavigate();

    const defaultValues = useMemo(
        () => ({
            customer_id: student?.customer_id || '',
            service_id: student?.service_id || '',
            from: student?.from || '',
            paid: student?.paid || false,
            // branch_id: student?.branch_id || '',
            notes: student?.notes || '',
            status: student?.status || '',
        }),
        [student],
    );

    const form = useForm<AddStudentFormValues | EditStudentFormValues>({
        resolver: zodResolver(student ? EditStudentSchema : AddStudentSchema),
        defaultValues,
    });

    const handleAddAnother = () => {
        setShowSuccessPopup(false);
        form.reset();
    };

    const onSubmit = async (data: AddStudentFormValues | EditStudentFormValues) => {
        setLoading(true);
        setError(null); // Clear previous errors
        try {
            await apiPost({
                endpoint: student ? endpoints.orders.update + student.id : endpoints.orders.create,
                body: data,
                config: { headers: { 'Content-Type': 'multipart/form-data' } },
            });
            setShowSuccessPopup(true);
        } catch (error: any) {
            if (error.response?.data?.errors) {
                // Handle field-specific errors
                Object.entries(error.response.data.errors).forEach(([field, messages]) => {
                    form.setError(field as any, {
                        type: 'manual',
                        message: (messages as string[]).join(', '),
                    });
                });
            } else {
                // Handle generic error
                let errorMessage = 'حدث خطأ أثناء إرسال البيانات. يرجى المحاولة مرة أخرى.';
                if (error.response) {
                    errorMessage = error.response.data.msg || errorMessage;
                } else if (error.request) {
                    errorMessage = 'تعذر الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت.';
                }
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="mb-8">
                    <ServiceInfo />

                    {error && (
                        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                            <div className="flex justify-between items-center">
                                <span>{error}</span>
                                <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
                                    ×
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex md:flex-row flex-col justify-end gap-3 items-center mt-8">
                        <div className="md:w-fit w-full">
                            {student ? (
                                <SecondaryButton
                                    type="button"
                                    className="xl:px-[101px] md:px-[60px] w-full md:py-[18px] py-[16px] text-[#A21515] border border-red-error-50 xl:font-bold bg-red-error-50"
                                    onClick={() => setShowCancelPopup(true)}
                                >
                                    إلغاء الطلب
                                </SecondaryButton>
                            ) : (
                                <SecondaryButton
                                    type="button"
                                    className="xl:px-[64.5px] md:px-[40px] w-full md:py-[18px] py-[16px] border border-r-red-error-50 bg-red-error-50 text-[#A21515] xl:font-bold"
                                    onClick={() => setShowCancelPopup(true)}
                                >
                                    الغاء
                                </SecondaryButton>
                            )}
                        </div>
                        <div className="md:w-fit w-full md:px-5">
                            {student ? (
                                <PrimaryButton
                                    className="xl:px-[107px] md:px-[60px] md:py-[18px] py-[16px] xl:font-bold md:ms-6 w-full"
                                    disabled={loading}
                                >
                                    {loading ? 'جاري التحميل...' : 'حفظ التغييرات'}
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    className="xl:px-[107px] md:px-[60px] md:py-[18px] py-[16px] xl:font-bold md:ms-6 w-full"
                                    disabled={loading}
                                >
                                    {loading ? 'جاري التحميل...' : 'اضافة'}
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </form>
            </Form>

            <CancelPopup
                open={showCancelPopup}
                onClickButton1={() => {
                    setShowCancelPopup(false);
                }}
                onClickButton2={() => {
                    navigate('/orders');
                    setShowCancelPopup(false);
                }}
                text1={student ? 'إلغاء تعديل بيانات الطلب' : 'إلغاء إضافة طلب'}
                text2={
                    student
                        ? 'أنت على وشك إلغاء عملية تعديل بيانات الطلب. سيتم تجاهل جميع البيانات المدخلة، هل أنت متأكد؟'
                        : 'أنت على وشك إلغاء عملية إضافة طلب جديد دون إكمال البيانات المطلوبة. سيتم تجاهل جميع البيانات المدخلة، هل أنت متأكد؟'
                }
                textButton1="إكمال الطلب"
                textButton2="إلغاء الطلب"
            />

            <SuccessPopup
                open={showSuccessPopup}
                text1={student ? 'تم تعديل البيانات بنجاح' : 'تم إضافة طلب جديد بنجاح'}
                text2={student ? 'تم تعديل بيانات الطلب الجديد بنجاح.' : 'تم تسجيل بيانات الطلب الجديد بنجاح.'}
                textButton2={student ? 'تعديل بيانات اخرى' : 'إضافة طلب أخر'}
                textButton1="العودة لقائمة التسجيلات"
                onAddAnother={handleAddAnother}
                onReturnToList={() => {
                    navigate('/orders');
                    setShowSuccessPopup(false);
                }}
            />
        </>
    );
}
